import React, { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';

import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Typography,
  withTooltip,
} from '@crossnokaye/ux-common';
import {
  DropdownInput,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  InputInlineValueChangeLabel,
} from '@crossnokaye/ux-common/ckds';

import { EditSettingsFormData } from '@/features/equipment-browser/equipment-details/details-form/equipment-details-form.types';
import { useFieldIsDirty } from '@/features/equipment-browser/equipment-details/settings/components/use-field-is-dirty';
import { EnhancedSetting } from '@/features/equipment-browser/subscriptions/use-get-settings-values';
import { PRIVILEGES } from '@/hooks/access/access-table';
import { useActionPermission } from '@/hooks/use-action-permission';
import { Stack } from '@/ux-common/components/stack/stack';

const DropdownInputWithTooltip = withTooltip(DropdownInput);

interface SettingFieldDropdownProps {
  setting: EnhancedSetting;
  form: UseFormReturn<EditSettingsFormData>;
  /**
   * Required form path — see `SettingFieldSwitch` for why this is required
   * (`settings.<id>` vs `sequencerSettings.<id>` is owned by the dispatch
   * layer).
   */
  name: `settings.${string}` | `sequencerSettings.${string}`;
}

/**
 * Dropdown field component for enum/select settings.
 * Displays a list of options for selection.
 */
export const SettingFieldDropdown = ({
  setting,
  form,
  name,
}: SettingFieldDropdownProps): React.ReactElement => {
  const { isDisabled: disabledByPermissions, tooltipText } = useActionPermission({
    privilege: PRIVILEGES.writeCommands,
  });

  const [isOpen, setIsOpen] = useState(false);
  const options = setting.displayOptions || [];
  const hasChanged = useFieldIsDirty(form.control, name);

  const handleOpenChange = (open: boolean) => {
    if (disabledByPermissions) return;
    setIsOpen(open);
  };

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        const value = field.value?.toString() || '';
        const currentOption = options.find((opt) => opt.value === value);
        const currentValue = currentOption?.value || value;
        const originalValue = setting.value?.toString();

        return (
          <FormItem>
            <FormControl>
              <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
                <DropdownMenuTrigger asChild>
                  <DropdownInputWithTooltip
                    label={setting.label}
                    info={
                      <Stack className="max-w-64">
                        <Typography variant="h4">{setting.label}</Typography>
                        <Typography variant="body">{setting.description}</Typography>
                      </Stack>
                    }
                    infoTabStop={false}
                    value={currentValue}
                    triggered={isOpen}
                    placeholder={'Select an option'}
                    disabled={disabledByPermissions}
                    tooltipContent={disabledByPermissions ? tooltipText : undefined}
                    renderValue={() => {
                      return (
                        <p className={hasChanged ? 'text-ckds-input-fg-new' : ''}>
                          {currentValue?.toUpperCase()}
                        </p>
                      );
                    }}
                    inlineLabel={
                      hasChanged ? (
                        <InputInlineValueChangeLabel
                          className={'ml-2'}
                          oldValue={originalValue?.toUpperCase() ?? ''}
                        />
                      ) : undefined
                    }
                  />
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="w-[--radix-dropdown-menu-trigger-width]"
                  withPortal
                >
                  {options.map((option) => (
                    <DropdownMenuItem
                      key={option.value}
                      onClick={() => {
                        field.onChange(option.value);
                        setIsOpen(false);
                      }}
                    >
                      <div className="flex items-center justify-between w-full">
                        <span className="font-medium">{option.label}</span>
                      </div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </FormControl>
            <FormMessage className={'pl-2'} />
          </FormItem>
        );
      }}
    />
  );
};
